/* Cloud-Ownership zu einem anderen Reseller verschieben (Superadmin-only).
 *
 * Ändert ausschließlich cloud.reseller_id. Der Backend-Endpoint blockt bei
 * Namens-Kollision beim Ziel-Reseller (409) und liefert Warnungen zurück,
 * wenn ein Premium-Entitlement (Souvera) beim Ziel-Reseller fehlt.
 * Ruft POST /api/admin/clouds/{id}/move.
 */
import React, { useState } from "react";
import useSWR from "swr";
import { api, formatApiError } from "@/lib/api";
import { ArrowRightLeft, AlertTriangle, CheckCircle2 } from "lucide-react";

const fetcher = (u) => api.get(u).then((r) => r.data);

export default function CloudMovePanel({ cloudId, cloud, onMoved }) {
    const { data: resellers } = useSWR("/api/admin/resellers/options", fetcher);
    const [target, setTarget] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    const [result, setResult] = useState(null);

    const options = (resellers || []).filter(
        (r) => r.id !== cloud?.reseller_id);

    const move = async () => {
        if (!target) return;
        const targetName =
            options.find((r) => r.id === target)?.name || target;
        if (!window.confirm(
            `Move cloud "${cloud?.name}" to reseller "${targetName}"?\n\n` +
            `Only the reseller assignment is changed. VMs, ` +
            `database, S3 and all services remain unchanged. In case of a ` +
            `name collision at the target reseller the operation is aborted.`))
            return;
        setBusy(true);
        setErr(null);
        setResult(null);
        try {
            const { data } = await api.post(
                `/api/admin/clouds/${cloudId}/move`,
                { target_reseller_id: target });
            setResult(data);
            setTarget("");
            if (onMoved) await onMoved();
        } catch (e) {
            setErr(formatApiError(e));
        }
        setBusy(false);
    };

    return (
        <div data-testid="cloud-move-panel" className="cm-card p-5">
            <div className="cm-h3 mb-3 flex items-center gap-2">
                <ArrowRightLeft size={14} className="text-zinc-400" />
                Move to another reseller
            </div>
            <p className="text-xs text-zinc-400 mb-3">
                Transfers ownership of this cloud to another reseller. Only the
                reseller assignment is changed — no data is migrated. If the
                cloud name is already taken at the target reseller, the operation
                is blocked.
            </p>
            <div className="flex items-center gap-2">
                <select
                    data-testid="cloud-move-target-select"
                    className="cm-input text-xs flex-1 bg-zinc-950 border border-zinc-800 rounded px-2 py-1.5 text-zinc-200"
                    value={target}
                    disabled={busy}
                    onChange={(e) => setTarget(e.target.value)}
                >
                    <option value="">Choose target reseller…</option>
                    {options.map((r) => (
                        <option key={r.id} value={r.id}>
                            {r.name} {r.status === "suspended" ? "(suspended)" : ""}
                        </option>
                    ))}
                </select>
                <button
                    data-testid="cloud-move-button"
                    className="cm-btn-secondary text-xs flex items-center gap-1.5 shrink-0"
                    disabled={busy || !target}
                    onClick={move}
                >
                    <ArrowRightLeft size={11} />
                    {busy ? "Moving…" : "Move"}
                </button>
            </div>

            {result && (
                <div
                    data-testid="cloud-move-result"
                    className="mt-3 bg-zinc-950 border border-zinc-800 p-2.5 text-xs"
                >
                    <div className="flex items-center gap-1.5 text-emerald-400">
                        <CheckCircle2 size={13} />
                        Cloud moved to "{result.to_reseller_name}".
                    </div>
                    {(result.warnings || []).map((w, i) => (
                        <div
                            key={i}
                            data-testid="cloud-move-warning"
                            className="mt-2 flex items-start gap-1.5 text-amber-300 text-xs"
                        >
                            <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                            <span>{w}</span>
                        </div>
                    ))}
                </div>
            )}

            {err && (
                <div
                    data-testid="cloud-move-error"
                    className="mt-3 bg-rose-950/30 border border-rose-800 text-rose-300 text-xs p-2 font-mono"
                >
                    {err}
                </div>
            )}
        </div>
    );
}
